import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, LinearProgress, Snackbar, TableCell, TableRow } from '@mui/material';
import { NotificationsActive } from '@mui/icons-material';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import { Loader } from '../../../shared/components/Loader';
import { invoiceService } from '../../../shared/services/invoiceService';
import { ReminderItem } from '../../../shared/types/reminder';

const fmtDate = (d?: string) => {
    if (!d) return '-';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const day = String(dt.getDate()).padStart(2, '0');
    const month = String(dt.getMonth() + 1).padStart(2, '0');
    const year = dt.getFullYear();
    return `${day}-${month}-${year}`;
};

const normalizeReminders = (res: any): ReminderItem[] => {
    const outer = res?.data || res;
    const items = outer?.data || outer?.items || outer?.reminders || [];
    if (!Array.isArray(items)) return [];
    return items.map((r: any) => ({
        ...r,
        _id: r?._id ?? r?.id,
        title: r?.title || r?.type || 'Reminder',
        message: r?.message || r?.note || '',
        dueDate: r?.dueDate || r?.date || r?.reminderDate || '',
        invoiceNumber: r?.invoiceNumber || r?.invoice?.invoiceNumber || '',
        clientName: r?.clientName || r?.client?.companyName || '',
        yearForReminder: r?.yearForReminder ?? r?.invoice?.yearForReminder ?? null,
        optionForReminder: r?.optionForReminder || r?.invoice?.optionForReminder || null,
    }));
};

export const Reminders: React.FC = () => {
    const [items, setItems] = useState<ReminderItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'error' as 'error' | 'success' | 'info',
    });

    const fetchReminders = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await invoiceService.getReminders({ page: page + 1, limit: pageSize });
            setItems(normalizeReminders(res));
        } catch {
            setItems([]);
            setSnackbar({
                open: true,
                message: 'Failed to load reminders',
                severity: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    }, [page, pageSize]);

    useEffect(() => {
        fetchReminders();
    }, [fetchReminders]);

    const filteredItems = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((item) => {
            return (
                String(item.title || '').toLowerCase().includes(q) ||
                String(item.message || '').toLowerCase().includes(q) ||
                String(item.invoiceNumber || '').toLowerCase().includes(q) ||
                String(item.clientName || '').toLowerCase().includes(q)
            );
        });
    }, [items, search]);

    const paginatedItems = useMemo(() => {
        const start = page * pageSize;
        return filteredItems.slice(start, start + pageSize);
    }, [filteredItems, page, pageSize]);

    return (
        <div className="flex flex-col w-full min-h-full bg-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b border-gray-200 bg-white">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <NotificationsActive className="text-[#0EA5E9] w-8 h-8" />
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
                        Reminders
                    </h2>
                </div>
                <div className="mt-4 sm:mt-0">
                    <AppSearch
                        value={search}
                        onChange={(value) => {
                            setSearch(value);
                            setPage(0);
                        }}
                        onSearch={() => setPage(0)}
                        placeholder="Search reminders"
                        className="w-full sm:w-72"
                    />
                </div>
            </div>

            <div className="flex flex-1 flex-col p-6 sm:p-10 overflow-x-auto">
                <div className="flex flex-col w-full flex-1">
                    {isLoading && items.length > 0 && <LinearProgress className="mb-4" />}
                    {isLoading && items.length === 0 ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            minHeight="350px"
                            sx={{ backgroundColor: '#1f2937', borderRadius: '8px' }}
                        >
                            <Loader />
                        </Box>
                    ) : (
                        <AppTable
                            tableSx={{ tableLayout: 'auto' }}
                            head={
                                <TableRow className="bg-gray-100">
                                    <TableCell className="!font-semibold !text-gray-700">Title</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700 !whitespace-nowrap">Invoice</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700 !min-w-[200px]">Client</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700 !whitespace-nowrap">Year For Reminder</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700 !whitespace-nowrap">Option For Reminder</TableCell>
                                    <TableCell className="!font-semibold !text-gray-700 !whitespace-nowrap">Due Date</TableCell>
                                </TableRow>
                            }
                            isEmpty={filteredItems.length === 0}
                            emptyColSpan={6}
                            emptyMessage="No reminders found"
                        >
                            {paginatedItems.map((item, idx) => (
                                <TableRow
                                    key={item._id || item.id || `${item.invoiceNumber || 'rem'}-${idx}`}
                                    className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                >
                                    <TableCell className="!text-gray-900 !font-medium">{item.title || '-'}</TableCell>
                                    <TableCell className="!text-gray-700 !whitespace-nowrap">{item.invoiceNumber || '-'}</TableCell>
                                    <TableCell className="!text-gray-700">{item.clientName || '-'}</TableCell>
                                    <TableCell className="!text-gray-700 !whitespace-nowrap">
                                        {item.yearForReminder != null ? `${item.yearForReminder} Year` : '-'}
                                    </TableCell>
                                    <TableCell className="!text-gray-700">{item.optionForReminder || '-'}</TableCell>
                                    <TableCell className="!text-gray-700 !whitespace-nowrap">{fmtDate(item.dueDate)}</TableCell>
                                </TableRow>
                            ))}
                        </AppTable>
                    )}
                </div>

                <AppTablePagination
                    totalRows={filteredItems.length}
                    page={page}
                    pageSize={pageSize}
                    onPageChange={(_, p) => setPage(p - 1)}
                    onPageSizeChange={(size) => {
                        setPageSize(size);
                        setPage(0);
                    }}
                />
            </div>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};
